import { Text, Heading, Stack, ModalContent, ModalCloseButton, ModalProps } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import CustomFieldBuilder from 'components/CustomFieldBuilder';
import Loader from 'components/Loader';
import { ControllableModal } from 'components/Modal';
import { TIMESHEET_DISPLAY_TYPES } from 'global/options';
import { ICustomFieldItem, useFetchPaginatedCustomFields } from 'hooks/customField/useFetchPaginatedCustomFields';
import { hasCustomFieldAccess, transformCustomFieldsToFormData } from 'utils/customFieldsUtils';

interface IConfirmationModal {
  title: string;
  subTitle?: string;
  type: 'clock_in' | 'clock_out';
  isOpen: boolean;
  onClose: () => void;
  hideCloseButton?: boolean;
  body?: React.ReactNode;
  modalProps?: ModalProps;
  isConfirmButtonLoading: boolean;
  jobId?: string;
  handleConfirmButtonClick: (customFields: Array<ICustomFieldItem> | null, values: any) => void;
}

const CustomFieldModal: React.FC<IConfirmationModal> = ({
  type,
  title,
  subTitle,
  isConfirmButtonLoading,
  isOpen,
  onClose,
  modalProps,
  jobId,
  handleConfirmButtonClick,
}) => {
  const { t } = useTranslation(['common']);

  const hasAccess = hasCustomFieldAccess();

  const [filteredCustomFields, setFilteredCustomFields] = useState<ICustomFieldItem[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [step, setStep] = useState<'custom-field' | 'confirmation-message'>('custom-field');

  useEffect(() => {
    if (hasAccess) {
      setStep('custom-field');
    } else {
      setStep('confirmation-message');
    }
  }, [isOpen, hasAccess]);

  const { data: customFieldsData, isFetching } = useFetchPaginatedCustomFields(
    {
      page: 1,
      page_size: 500,
      module: 'timesheets',
      active: '1',
      display_on: type === TIMESHEET_DISPLAY_TYPES.CLOCK_IN ? TIMESHEET_DISPLAY_TYPES.CLOCK_IN : undefined,
      job_id: jobId,
    },
    {
      enabled: isOpen && hasAccess && step === 'custom-field',
      onSuccess: (data: any) => {
        if (!Array.isArray(data?.payload) || !data?.payload.length) {
          setStep('confirmation-message');
          return;
        }

        // If type is clock out, then show clock out + timesheet fields
        const filteredCustomFields =
          type === TIMESHEET_DISPLAY_TYPES.CLOCK_OUT
            ? data.payload.filter((field: any) => field?.display_on !== TIMESHEET_DISPLAY_TYPES.CLOCK_IN)
            : data.payload;

        if (!filteredCustomFields.length) {
          setStep('confirmation-message');
          return;
        }

        setFilteredCustomFields(filteredCustomFields);
        setFormData(transformCustomFieldsToFormData(filteredCustomFields));
      },
    }
  );

  const handleFormSubmit = () => {
    setStep('confirmation-message');
  };

  return (
    <ControllableModal isOpen={isOpen} onClose={onClose} {...modalProps}>
      <ModalContent p="6" pt="10" textAlign="center">
        <ModalCloseButton />
        <Formik initialValues={formData} enableReinitialize onSubmit={handleFormSubmit}>
          {({ values }) => (
            <Form noValidate>
              {isFetching && step === 'custom-field' ? <Loader height="150px" /> : null}

              {/* Render Custom Field */}
              {!isFetching && step === 'custom-field' && (
                <>
                  <Heading as="h6" size="md" mb="6">
                    Fill Custom Fields Before Clocking {type === 'clock_in' ? 'In' : 'Out'}
                  </Heading>
                  {Array.isArray(filteredCustomFields) && filteredCustomFields?.length
                    ? filteredCustomFields.map(customField => {
                        return (
                          <div key={customField.id}>
                            <CustomFieldBuilder customField={customField as ICustomFieldItem} />
                          </div>
                        );
                      })
                    : null}
                  <Stack direction="row" justifyContent="center" mt="6">
                    <Button variant="ghost" minW="170px" onClick={onClose}>
                      {t('common:cancel')}
                    </Button>
                    <Button type="submit" minWidth="170px" fontWeight="semibold">
                      Submit
                    </Button>
                  </Stack>
                </>
              )}

              {/* Render Confirmation Message */}
              {step === 'confirmation-message' && (
                <>
                  <Heading as="h6" size="md" mb="2">
                    {title}
                  </Heading>
                  <Text color="gray.600">{subTitle}</Text>
                  <Stack direction="row" justifyContent="center" mt="6">
                    <Button variant="ghost" minW="170px" onClick={onClose}>
                      {t('common:cancel')}
                    </Button>
                    <Button
                      minWidth="170px"
                      fontWeight="semibold"
                      bg="rgba(38, 103, 201, 0.1)"
                      color="primary.500"
                      _hover={{
                        bg: 'rgba(8, 22, 42, 0.2)',
                      }}
                      _active={{
                        bg: 'rgba(38, 103, 201, 0.2)',
                      }}
                      isLoading={isConfirmButtonLoading}
                      onClick={() =>
                        handleConfirmButtonClick(hasAccess ? customFieldsData?.payload || [] : null, values)
                      }
                    >
                      {type === 'clock_in' ? 'Clock In' : 'Clock Out'}
                    </Button>
                  </Stack>
                </>
              )}
            </Form>
          )}
        </Formik>
      </ModalContent>
    </ControllableModal>
  );
};

export default memo(CustomFieldModal);
